// Turns a pasted or uploaded schedule into the rows POST /growth/previews takes.
//
// A preview holds team-level facts only, so this reads the columns it knows and
// leaves every other one in the browser: a Player, Email or Photo column is never
// sent. The server drops them too (preview-import.ts); that's the control, this
// is so nothing about a person leaves the operator's screen.

import { parseCsv } from './csv'
import type { PreviewGameRow } from '@/api/types'

type GameField = keyof PreviewGameRow | 'score'

const HEADERS: Record<string, GameField> = {
  home: 'home_team', 'home team': 'home_team', home_team: 'home_team', 'team 1': 'home_team',
  away: 'away_team', 'away team': 'away_team', away_team: 'away_team', visitor: 'away_team', visitors: 'away_team', 'visiting team': 'away_team', 'team 2': 'away_team',
  date: 'game_date', 'game date': 'game_date', game_date: 'game_date',
  time: 'game_time', 'game time': 'game_time', game_time: 'game_time', 'start time': 'game_time',
  timezone: 'timezone', 'time zone': 'timezone',
  status: 'status',
  'home score': 'home_score', home_score: 'home_score',
  'away score': 'away_score', away_score: 'away_score', 'visitor score': 'away_score',
  score: 'score', result: 'score',
  round: 'round_number', round_number: 'round_number', week: 'round_number', matchday: 'round_number',
}

export interface ParsedGames {
  rows: PreviewGameRow[]
  /** The file's columns that stay behind, as the file names them. */
  leftOut: string[]
  /** home, away or date has no column: nothing can be read. */
  missing: string[]
}

/** 9/5/2026 or 09-05-26 into 2026-09-05; anything else as it came, for the server to judge. */
export function isoDate(raw: string): string {
  const s = raw.trim()
  const m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(s)
  if (!m) return s
  const year = m[3].length === 2 ? `20${m[3]}` : m[3]
  return `${year}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`
}

/** 6:30 PM into 18:30; anything else as it came. */
export function clockTime(raw: string): string {
  const s = raw.trim()
  const m = /^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?$/i.exec(s)
  if (!m) return s
  const hour = (Number(m[1]) % 12) + (m[3].toLowerCase() === 'p' ? 12 : 0)
  return `${String(hour).padStart(2, '0')}:${m[2] ?? '00'}`
}

export function parseGames(text: string): ParsedGames {
  if (!text.trim()) return { rows: [], leftOut: [], missing: [] }
  const { headers, rows } = parseCsv(text)
  const fields = headers.map((h) => HEADERS[h.trim().toLowerCase()] ?? null)
  const leftOut = headers.filter((h, i) => fields[i] === null && h.trim())
  const missing = [
    ['home_team', 'Home'],
    ['away_team', 'Away'],
    ['game_date', 'Date'],
  ].filter(([f]) => !fields.includes(f as GameField)).map(([, label]) => label)

  const parsed = rows.map((cells) => {
    const row: PreviewGameRow = {}
    fields.forEach((field, i) => {
      const value = cells[i]?.trim()
      if (!field || !value) return
      if (field === 'score') {
        const score = /^(\d+)\s*[-–:]\s*(\d+)$/.exec(value)
        if (score) {
          row.home_score = score[1]
          row.away_score = score[2]
        }
      } else if (field === 'game_date') row.game_date = isoDate(value)
      else if (field === 'game_time') row.game_time = clockTime(value)
      else row[field] = value
    })
    return row
  })
  return { rows: parsed, leftOut, missing }
}

/**
 * Team names, one per line. A file with a Team or Name column works too; the
 * first column of a line with commas is the name, the rest stays behind.
 */
export function parseTeams(text: string): { rows: string[]; leftOut: string[] } {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
  if (lines.length === 0) return { rows: [], leftOut: [] }
  if (!/[,;\t]/.test(lines[0])) return { rows: lines.filter((l) => !/^(team|teams|name|team name)$/i.test(l)), leftOut: [] }
  const { headers, rows } = parseCsv(text)
  const at = Math.max(0, headers.findIndex((h) => /^(team|team name|name)$/i.test(h.trim())))
  return { rows: rows.map((r) => r[at]?.trim()).filter(Boolean), leftOut: headers.filter((h, i) => i !== at && h.trim()) }
}
